<script setup lang="ts">
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { useValidators } from "@/composables/useValidators";
import { getCountryFromEecs, isEecsAccountIdentifier } from "@/utils/eecs";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import SegmentedControl from "@/components/common/SegmentedControl.vue";
import InfoCallout from "@/components/common/InfoCallout.vue";
import type { CnmcRecipient } from "./cnmcTypes";

const props = withDefaults(
  defineProps<{
    destinations: { region: string; name: string }[];
    presets?: CnmcRecipient[];
    domestic?: boolean;
    required?: boolean;
  }>(),
  { presets: () => [], domestic: false, required: true }
);
const model = defineModel<CnmcRecipient>({ required: true });
const id = useId();
const { t } = useI18n();
const validators = useValidators(t);
const required = validators.requiredIf(
  () =>
    props.required ||
    Boolean(
      model.value.name ||
      model.value.region ||
      model.value.eecs ||
      model.value.r2
    )
);
const rules = computed(() => ({
  name: { required, maxLength: validators.maxLength(128) },
  region: { required },
  r2:
    model.value.region === "ES"
      ? {
          required,
          format: helpers.withMessage(
            () => t("cnmc.validation.r2"),
            helpers.regex(/^R2-.+$/)
          ),
        }
      : {},
  eecs:
    model.value.region !== "ES"
      ? {
          required,
          format: helpers.withMessage(
            () => t("validations.eecs_account_format"),
            (value: string) =>
              !helpers.req(value) || isEecsAccountIdentifier(value)
          ),
          region: helpers.withMessage(
            () => t("cnmc.validation.eecs_region"),
            (value: string) =>
              !isEecsAccountIdentifier(value) ||
              !model.value.region ||
              getCountryFromEecs(value) === model.value.region
          ),
        }
      : {},
}));
const v$ = useVuelidate(
  rules,
  computed(() => ({
    ...model.value,
    r2: model.value.r2 ?? "",
    eecs: model.value.eecs ?? "",
  })),
  { $autoDirty: true }
);
const recipientOptions = computed(() => [
  { value: "EXPORT", label: t("cnmc.eecs_recipient") },
  { value: "DOMESTIC", label: t("cnmc.domestic_recipient") },
]);
const setRegion = (region: string) => {
  model.value = {
    ...model.value,
    region,
    eecs: "",
    r2: null,
  };
};
const setField = (field: "name" | "eecs" | "r2" | "region", event: Event) => {
  const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
  if (field === "region") {
    setRegion(value);
    return;
  }
  model.value = {
    ...model.value,
    [field]: value,
  };
};
</script>

<template>
  <div class="row g-3">
    <div v-if="domestic" class="col-12">
      <label class="form-label">{{ $t("cnmc.recipient_type") }}</label>
      <SegmentedControl
        :modelValue="model.region === 'ES' ? 'DOMESTIC' : 'EXPORT'"
        :options="recipientOptions"
        :aria-label="$t('cnmc.recipient_type')"
        @update:modelValue="setRegion($event === 'DOMESTIC' ? 'ES' : '')"
      />
      <InfoCallout icon="file-lines" class="mt-3">
        {{
          $t(
            model.region === "ES"
              ? "cnmc.domestic_delivery_help"
              : "cnmc.export_delivery_help"
          )
        }}
      </InfoCallout>
    </div>
    <div
      v-if="presets.length && model.region !== 'ES'"
      class="col-12 d-flex flex-wrap gap-2"
    >
      <button
        v-for="preset in presets"
        :key="`${preset.region}:${preset.eecs}`"
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="model = { ...preset }"
      >
        {{ preset.name }} · {{ preset.region }} · {{ preset.eecs }}
      </button>
    </div>
    <div class="col-12">
      <label :for="`${id}-name`" class="form-label">{{
        $t("cnmc.recipient_name")
      }}</label>
      <input
        :id="`${id}-name`"
        :value="model.name"
        @input="setField('name', $event)"
        class="form-control"
        :class="{ 'is-invalid': v$.name.$error }"
        :aria-invalid="v$.name.$error"
        @blur="v$.name.$touch()"
      />
      <CnmcFieldErrors :validation="v$.name" />
    </div>
    <div v-if="model.region !== 'ES'" class="col-md-6">
      <label :for="`${id}-region`" class="form-label">{{
        $t("cnmc.destination_registry")
      }}</label>
      <select
        :id="`${id}-region`"
        :value="model.region"
        class="form-select"
        :class="{ 'is-invalid': v$.region.$error }"
        :aria-invalid="v$.region.$error"
        @blur="v$.region.$touch()"
        @change="setField('region', $event)"
      >
        <option value="" disabled>{{ $t("cnmc.choose_registry") }}</option>
        <option
          v-for="destination in destinations.filter(
            (item) => item.region !== 'ES'
          )"
          :key="destination.region"
          :value="destination.region"
        >
          {{ destination.name }} ({{ destination.region }})
        </option>
      </select>
      <CnmcFieldErrors :validation="v$.region" />
    </div>
    <div v-if="model.region === 'ES'" class="col-md-6">
      <label :for="`${id}-r2`" class="form-label">{{
        $t("cnmc.recipient_r2")
      }}</label>
      <input
        :id="`${id}-r2`"
        :value="model.r2"
        @input="setField('r2', $event)"
        class="form-control"
        :class="{ 'is-invalid': v$.r2.$error }"
        :aria-invalid="v$.r2.$error"
        @blur="v$.r2.$touch()"
        placeholder="R2-…"
      />
      <CnmcFieldErrors :validation="v$.r2" />
      <div class="form-text">{{ $t("cnmc.r2_help") }}</div>
    </div>
    <div v-else class="col-md-6">
      <label :for="`${id}-eecs`" class="form-label">{{
        $t("cnmc.eecs_account")
      }}</label>
      <input
        :id="`${id}-eecs`"
        :value="model.eecs"
        class="form-control text-uppercase"
        :class="{ 'is-invalid': v$.eecs.$error }"
        :aria-invalid="v$.eecs.$error"
        @blur="v$.eecs.$touch()"
        @input="setField('eecs', $event)"
      />
      <CnmcFieldErrors :validation="v$.eecs" />
    </div>
  </div>
</template>
